/**
 * Where someone goes when the app cannot help them.
 *
 * One constant because there are now three places that need it, and the sign-in screen
 * is the one that matters most: a person who cannot get in has no menu to reach, so
 * whatever is on that page is their only route to a human.
 */
export const SUPPORT_URL = 'https://support.dhigroup.com/support/';

/** Where Customer Care knowledge-base articles live, as the KB ingestion stamps them. */
export const KB_ARTICLE_URL = 'https://support.dhigroup.com/knowledgebase/article';

/**
 * Whether a link is a Customer Care article. The portal refuses to be framed, so these
 * always open in a new tab, never in the side panel's page viewer.
 */
export function isKbUrl(url: string | null | undefined): boolean {
  return !!url && url.startsWith(KB_ARTICLE_URL);
}

/**
 * Microsoft Form for general product feedback, opened by the floating button.
 *
 * General feedback has no trace to hang on, so it deliberately does not go through
 * `POST /feedback` (which scores a Langfuse trace). The form is set to "anyone can
 * respond" because beta participants are outside the DHI tenant, and to notify on
 * each response, which is why there is no backend, no storage and no notification
 * wiring on our side. The price is that submissions are anonymous and carry no app
 * context.
 *
 * Empty string hides the button, so a deployment without a form shows nothing
 * rather than a link to nowhere.
 */
export const FEEDBACK_FORM_URL = 'https://forms.cloud.microsoft/e/BRH6F7EuMZ';

/**
 * The documents the app has to keep permanently reachable, so that a notice shown
 * once at first run stays findable afterwards.
 *
 * An empty URL renders the entry disabled rather than hiding it, which is the
 * opposite of what an empty FEEDBACK_FORM_URL does above, and the difference is
 * deliberate. A missing feedback form is nothing to a user. A missing privacy
 * notice is a gap somebody should be able to see. None of the three documents is
 * published yet, so all three are empty today, and publishing one is a one-line
 * change here rather than a component edit.
 */
export const LEGAL_LINKS: { key: string; label: string; url: string }[] = [
  { key: 'privacy', label: 'Privacy notice', url: '' },
  { key: 'terms', label: 'Terms of use', url: '' },
  { key: 'disclaimer', label: 'Disclaimer', url: '' },
];
